import { computed, watch, onMounted, onUnmounted } from 'vue';
import { useStore } from 'dashboard/composables/store';

/**
 * Composable que aplica restrições de UI para agentes via CSS.
 * Injeta stylesheet dinâmica baseada no role do usuário.
 * - Vendedor (agent com custom_role Vendedor): restrições completas
 * - Supervisor (agent com custom_role Supervisor): restrições parciais
 * - Admin: sem restrições
 */
export function useCentertelRestrictions() {
  const store = useStore();

  const currentRole = computed(() => store.getters.getCurrentRole);
  const currentUser = computed(() => store.getters.getCurrentUser);
  const isAgent = computed(() => currentRole.value === 'agent');
  const isAdmin = computed(() => currentRole.value === 'administrator');

  // Detectar supervisor via custom role store OU body class (fallback)
  const customRoleId = computed(() => store.getters.getCurrentCustomRoleId);
  const customRoles = computed(() => store.getters['customRole/getCustomRoles'] || []);
  const customRoleName = computed(() => {
    if (!customRoleId.value) return null;
    const role = customRoles.value.find(r => r.id === customRoleId.value);
    return role?.name || null;
  });
  const isSupervisor = computed(() => {
    if (!isAgent.value) return false;
    if (customRoleName.value === 'Supervisor') return true;
    return document.body.classList.contains('ct-supervisor');
  });
  const isVendedor = computed(() => isAgent.value && !isSupervisor.value && !isAdmin.value);

  const STYLE_ID = 'centertel-restrictions';

  // CSS para TODOS os agentes (vendedor + supervisor)
  const agentCSS = `
    /* ── Sidebar: esconder seções ── */
    /* Pastas, Canais, Etiquetas na sidebar */
    [data-key="list-folders"],
    [data-key="list-channels"],
    [data-key="list-labels"],
    [data-key="list-teams"] { display: none !important; }

    /* Capitão/Captain, Empresas/Companies, Central de Ajuda */
    a[href*="captain"], a[href*="companies"], a[href*="portals"],
    a[href*="contacts"], a[href*="reports"], a[href*="campaigns"],
    a[href*="settings"] { display: none !important; }

    /* Esconder grupos pai (li) que contém links de seções restritas */
    li:has(a[href*="contacts"]),
    li:has(a[href*="settings"]),
    li:has(a[href*="companies"]),
    li:has(a[href*="reports"]),
    li:has(a[href*="campaigns"]),
    li:has(a[href*="captain"]),
    li:has(a[href*="portals"]) { display: none !important; }

    /* Botão editar na sidebar */
    aside button:has(.i-lucide-pen-line) { display: none !important; }

    /* Ícone merge na conversa */
    .i-ph-arrows-merge { display: none !important; }

    /* Botão Contatos (ícone user) no header */
    button:has(> .i-ph-user-bold) { display: none !important; }

    /* Botão Captain/Copilot */
    button:has(> .i-woot-captain) { display: none !important; }

    /* ── Conversa: esconder botões ── */
    /* 3 pontos (more actions) */
    .actions--container button:has(.i-lucide-more-vertical),
    .actions--container button:has(.i-lucide-ellipsis) { display: none !important; }

    /* Seta dropdown do resolve */
    .resolve-actions button:has(.i-lucide-chevron-down) { display: none !important; }

    /* ── Menu perfil: esconder opções ── */
    /* Seção de status (Disponibilidade + Marcar offline automaticamente) */
    /* Seletor restrito ao dropdown do profile menu (bottom da sidebar) */
    aside section .n-dropdown-section { display: none !important; }
    /* Separador abaixo do status */
    aside section .n-dropdown-separator { display: none !important; }
    /* Atalhos do teclado */
    .n-dropdown-item:has(.i-lucide-keyboard) { display: none !important; }
    /* Configurações do Perfil */
    .n-dropdown-item:has(.i-lucide-user-pen) { display: none !important; }
    /* Aparência */
    .n-dropdown-item:has(.i-lucide-palette) { display: none !important; }

    /* ── Lista: esconder status@broadcast ── */
    .conversation-item:has([title*="status@broadcast"]) { display: none !important; }

    /* ── Busca da sidebar: esconder ── */
    aside button:has(.i-lucide-search),
    aside [data-key="search"],
    aside input[type="search"],
    aside .search-input { display: none !important; }

    /* ── Filtros: esconder ── */
    button:has(.i-lucide-list-filter) { display: none !important; }
  `;

  // CSS SOMENTE para vendedores (não supervisores)
  const vendedorCSS = `
    /* Esconder nome do agente atribuído na lista de conversas */
    /* Vendedor só vê as próprias conversas, nome é redundante */
    span.inline-flex.items-center.gap-px.truncate:has(.i-lucide-user-round) { display: none !important; }
  `;

  const globalCSS = `
    /* ── Esconder IDs WAHA em qualquer lugar ── */
    .conversation--header--actions span[title*="@c.us"],
    .conversation--header--actions span[title*="@lid"],
    .conversation--header--actions span[title*="@s.whatsapp.net"] {
      display: none !important;
    }

    /* ── Botão CRM na sidebar ── */
    .ct-crm-btn {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      padding: 6px 8px;
      border: none;
      border-radius: 8px;
      background: transparent;
      color: #f59e0b;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: background 0.15s, color 0.15s;
      text-decoration: none;
    }
    .ct-crm-btn:hover {
      background: var(--n-alpha-2, rgba(255,255,255,0.08));
      color: #fbbf24;
    }
    .ct-crm-btn svg {
      width: 16px;
      height: 16px;
      flex-shrink: 0;
      color: #f59e0b;
    }
    /* Quando sidebar está colapsada */
    .ct-crm-btn.collapsed {
      justify-content: center;
      padding: 6px;
    }
    .ct-crm-btn.collapsed span { display: none; }
  `;

  const injectStyles = () => {
    // Remover anterior
    const existing = document.getElementById(STYLE_ID);
    if (existing) existing.remove();

    const style = document.createElement('style');
    style.id = STYLE_ID;

    let css = globalCSS;
    if (isAgent.value && !isAdmin.value) {
      css += agentCSS;
    }
    if (isVendedor.value) {
      css += vendedorCSS;
    }

    style.textContent = css;
    document.head.appendChild(style);
  };

  const removeStyles = () => {
    const existing = document.getElementById(STYLE_ID);
    if (existing) existing.remove();
  };

  // Injetar botão CRM acima do avatar na sidebar
  const CRM_BTN_ID = 'ct-crm-sidebar-btn';
  const injectCRMButton = () => {
    if (document.getElementById(CRM_BTN_ID)) return;

    // Encontrar o container do profile menu (último div com border-t na sidebar)
    const profileContainer = document.querySelector(
      'aside section > div.border-t'
    );
    if (!profileContainer) return;

    const link = document.createElement('a');
    link.id = CRM_BTN_ID;
    link.href = 'https://ennychat.com.br/mural';
    link.className = 'ct-crm-btn';
    link.title = 'Voltar ao CRM';
    link.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
        <polyline points="15 3 21 3 21 9"/>
        <line x1="10" y1="14" x2="21" y2="3"/>
      </svg>
      <span>CRM</span>
    `;

    profileContainer.insertAdjacentElement('beforebegin', link);

    // Observar mudanças de collapsed na sidebar para atualizar o botão
    const observer = new MutationObserver(() => {
      const aside = document.querySelector('aside');
      if (!aside) return;
      const isCollapsed = aside.offsetWidth < 100;
      link.classList.toggle('collapsed', isCollapsed);
    });
    const aside = document.querySelector('aside');
    if (aside) {
      observer.observe(aside, { attributes: true, attributeFilter: ['style', 'class'] });
    }
  };

  const removeCRMButton = () => {
    const btn = document.getElementById(CRM_BTN_ID);
    if (btn) btn.remove();
  };

  // Bloquear context menu na lista para vendedores
  const handleContextMenu = (e) => {
    if (isAgent.value && !isAdmin.value) {
      const convList = e.target.closest('.conversations-list');
      if (convList) {
        e.preventDefault();
        e.stopPropagation();
      }
    }
  };

  onMounted(() => {
    injectStyles();
    document.addEventListener('contextmenu', handleContextMenu, true);
    // Injetar CRM com retry (sidebar pode não estar montada ainda)
    setTimeout(injectCRMButton, 500);
    setTimeout(injectCRMButton, 2000);
  });

  onUnmounted(() => {
    removeStyles();
    removeCRMButton();
    document.removeEventListener('contextmenu', handleContextMenu, true);
  });

  // Reinjetar se role ou user mudar
  // Reinjetar quando role, user ou customRoleName mudar
  watch([currentRole, currentUser, customRoleName], injectStyles);

  return {
    isVendedor,
    isSupervisor,
    isAdmin,
    currentRole,
  };
}
